import React, { useEffect, useRef } from "react";
import { type FormikProps } from "formik";
import { type CampaignOption, type CampaignFormFields, type CampaignFormProps } from "../types";
import { extractFormikProps } from "utils/formikUtils";
import { nameofFactory } from "utils/nameof";
import { ValidatedForm } from "components/forms";
import MultiSelectWithAdditionField from "components/forms/MultiSelectWithAdditionField";
import { type TagsPayloadCamelPlural } from "interfaces/Tags";
import { type NormalizedDropdown } from "utils/miscellaneousUtils";
import { isEmpty, noop } from "lodash";
import { Header } from "semantic-ui-react";
import { PriorityLevels } from "enums";
import NextFlowModal from "features/Library/Flows/Designer/FlowComponentsPanel/flowEnd/nextFlowModal/NextFlowModal";
import { type OnTriggerModalObserver } from "features/Library/Flows/Designer/ReactFlowCanvas/nodes/StartOfTheFlow/StartOfTheFlowCardWrapper/StartOfTheFlowCardWrapper";
import Observable from "utils/Observable";
import { type FlowEndInfo } from "features/Library/Flows/Designer/types";
import PriorityLevelDropdown from "components/priorityLevels/priorityLevelDropdown/PriorityLevelDropdown";
import LabelWithInfoTooltip from "components/labelWithInfoTooltip/labelWithInfoTooltip";
import cn from "classnames";

import styles from "./campaignForm.module.scss";
import FlexibleDaysToCompleteInput from "components/priorityLevels/flexibleDaysToCompleteInput/FlexibleDaysToCompleteInput";
import { PeopleType } from "features/Library/PeopleAssignments/types";
import { ErrorPositions } from "enums/errorPositions";

export type CampaignFormPropsAll = {
  types: CampaignOption[];
  triggerTypes: CampaignOption[];
  subdomains: CampaignOption[];
  normalizedTags?: TagsPayloadCamelPlural;
  isReadOnlyView?: boolean;
} & FormikProps<CampaignFormFields> &
  CampaignFormProps;

const CampaignForm = (props: CampaignFormPropsAll) => {
  const {
    values,
    isLoading,
    isLoadingOptions,
    disablePreventTransitionPrompt,
    onBlur,
    save,
    disabled,
    types,
    triggerTypes,
    subdomains,
    normalizedTags,
  } = props;
  const formik = extractFormikProps<CampaignFormFields, CampaignFormPropsAll>(props);

  const nameof = nameofFactory<CampaignFormFields>();

  const onTriggerModalObserver = useRef<OnTriggerModalObserver>(new Observable());

  const isTriggerTypeNotSelected = (): boolean => !values.remedialTraining.triggerTypeId;

  const isFlowNotSelected = (): boolean => !values.remedialTraining.flowId;

  const setSelectedFlow = async (flowTitle: string, flowId?: number) => {
    await formik.setValues((prev) => {
      return {
        ...prev,
        remedialTraining: {
          ...prev.remedialTraining,
          flowTitle,
          flowId,
        },
      };
    }, true);
    save();
  };

  const onSelectedFlowTitleClear = async () => {
    if (!values.remedialTraining.flowTitle && !!values.remedialTraining.triggerTypeId) {
      await setSelectedFlow("");
    }
  };

  useEffect(() => {
    onSelectedFlowTitleClear();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [values.remedialTraining.flowTitle]);

  const dropdownSelector = ({ id, name }: CampaignOption) => ({
    key: id,
    value: id,
    text: name,
  });

  const campaignTypesOptions = types.map(dropdownSelector);
  const campaignSubdomainsOptions = subdomains.map(dropdownSelector);
  const triggerOptions = triggerTypes.map(dropdownSelector);

  const handleTagChange = async (field: "labels", value: NormalizedDropdown, shouldValidate?: boolean) => {
    const errors = await (formik.setFieldValue(field, value.selected, shouldValidate) as unknown as Promise<void>);
    if (isEmpty(errors?.[field])) {
      save();
    }
  };

  const browseFlows = () => {
    onTriggerModalObserver.current.notify(noop);
  };

  const onRemedialTrainingSelected = async (flowEndInfo: FlowEndInfo) => {
    const selectedFlow = flowEndInfo.nextFlow;
    if (!selectedFlow) {
      return;
    }
    await setSelectedFlow(selectedFlow.title, selectedFlow.id);
  };

  const onPriorityFieldChange = (propertyName: string) => async (value: any) => {
    await formik.setFieldValue(`remedialTraining.${propertyName}`, value, true);
    save();
  };

  const renderCampaignInformation = () => (
    <div className={styles.campaign}>
      <ValidatedForm.InputField
        {...formik}
        label="Campaign Name"
        value={values.name}
        propertyName={nameof("name")}
        placeholder="Enter Campaign Name"
        onBlur={onBlur}
        markAsRequired
        disabled={disabled}
        isReadOnlyView={props.isReadOnlyView}
      />
      <ValidatedForm.DropdownField
        {...formik}
        label="Campaign Type"
        value={values.campaignTypeId}
        propertyName="campaignTypeId"
        placeholder="Select Campaign Type"
        options={campaignTypesOptions}
        isLoading={isLoadingOptions}
        markAsRequired
        disabled={disabled}
        onBlur={onBlur}
        onChangeCallback={save}
        testID="campaign-type-dropdown"
        isReadOnlyView={props.isReadOnlyView}
      />
      <ValidatedForm.DropdownField
        {...formik}
        label="Subdomain"
        value={values.subDomainId}
        propertyName="subDomainId"
        placeholder="Select Subdomain"
        options={campaignSubdomainsOptions}
        isLoading={isLoadingOptions}
        markAsRequired
        disabled={disabled}
        onBlur={onBlur}
        onChangeCallback={save}
        testID="campaign-subdomain-dropdown"
        isReadOnlyView={props.isReadOnlyView}
      />
      <MultiSelectWithAdditionField
        {...formik}
        isCaseSensitive
        shouldValidate
        label="Labels"
        propertyName="labels"
        disabled={disabled}
        options={normalizedTags?.labels}
        value={values.labels}
        setFieldValue={handleTagChange}
        isReadOnlyView={props.isReadOnlyView}
        readOnlyEmptyState={"No labels selected"}
      />
    </div>
  );

  const renderRemedialTraining = () => (
    <div className={styles.remedial}>
      <ValidatedForm.DropdownField
        {...formik}
        label="Trigger"
        value={values.remedialTraining.triggerTypeId}
        propertyName="remedialTraining.triggerTypeId"
        placeholder="Select a trigger"
        options={triggerOptions}
        isLoading={isLoadingOptions}
        disabled={disabled}
        onBlur={onBlur}
        onChangeCallback={save}
        clearable
        testID="trigger-type-dropdown"
        isReadOnlyView={props.isReadOnlyView}
        readOnlyEmptyState="No trigger selected"
      />
      <div className={styles.flows}>
        <ValidatedForm.InputField
          {...formik}
          label="Remedial Training Flow"
          propertyName={"remedialTraining.flowTitle"}
          value={values.remedialTraining.flowTitle}
          className={styles.title}
          placeholder="Browse flows"
          action={{
            className: "basic",
            color: "blue",
            content: "Browse",
            onClick: browseFlows,
            disabled: disabled || isTriggerTypeNotSelected(),
          }}
          disabled={disabled || isTriggerTypeNotSelected()}
          markAsRequired={!!values.remedialTraining.triggerTypeId}
          clearable
          isReadOnlyView={props.isReadOnlyView}
          readOnlyEmptyState={"No Flow selected"}
        />
      </div>
      <div className={styles.priority}>
        <div className={styles.priorityLevel}>
          {!props.isReadOnlyView && (
            <LabelWithInfoTooltip
              width={16}
              label="Priority Level"
              info={
                "Set the relative priority of this content. Choose 'Required' to specify a number of days or a fixed completion date."
              }
              labelClass={cn({
                [styles.disabled]: disabled || isTriggerTypeNotSelected() || isFlowNotSelected(),
              })}
            />
          )}
          <PriorityLevelDropdown
            priorityLevel={values.remedialTraining.priority}
            onChange={async (priorityLevel: PriorityLevels) => {
              await onPriorityFieldChange("priority")(priorityLevel);
            }}
            disabled={disabled || isTriggerTypeNotSelected() || isFlowNotSelected()}
            isReadOnlyView={props.isReadOnlyView}
          />
        </div>
        {values.remedialTraining.priority === PriorityLevels.required && (
          <FlexibleDaysToCompleteInput
            {...formik}
            peopleType={PeopleType.User}
            daysToComplete={values.remedialTraining.daysToComplete}
            fixedDueDate={values.remedialTraining.fixedDueDate}
            isFixedDueDate={values.remedialTraining.isFixedDueDate}
            onChange={onPriorityFieldChange}
            disabled={disabled || isTriggerTypeNotSelected() || isFlowNotSelected()}
            nestedValuesPrefix="remedialTraining"
            errorPosition={ErrorPositions.right}
            isReadOnlyView={props.isReadOnlyView}
          />
        )}
      </div>
    </div>
  );

  const renderFields = (): React.ReactNode => {
    return (
      <>
        <div className={styles.root}>
          <Header className={styles.header}>Campaign Information</Header>
          {renderCampaignInformation()}
          <Header className={styles.header}>Remedial Training</Header>
          {renderRemedialTraining()}
        </div>
        <NextFlowModal
          onFlowEndChange={onRemedialTrainingSelected}
          onTriggerModalObserver={onTriggerModalObserver.current}
        />
      </>
    );
  };

  return (
    <ValidatedForm
      dirty={props.dirty}
      unsavedChangesPrompt={{
        title: "Exit Without Saving?",
        message: "Are you sure you want to exit without saving this campaign? All information entered will be lost.",
      }}
      disablePreventTransitionPrompt={disablePreventTransitionPrompt}
      isLoading={isLoading}
      formWidthComputer={6}
      withoutFormik
    >
      {renderFields}
    </ValidatedForm>
  );
};

export default CampaignForm;
